import { Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ChatAppResponse } from "../../api";
import { PriceSource, SiteId } from "../../api/models";
import { clearN8NSession } from "../../api/n8nAdapter";
import { clearAllSelections } from "../../components/Cards/selectionStore";
import { WebCitationItem } from "../../components/Answer/WebCitations";
import {
    clearProductThreadMessage,
    GENERAL_THREAD_KEY,
    isProductThreadKey,
    markProductThreadMessage,
    PRODUCT_THREAD_REGISTRY_KEY_PREFIX,
    productThreadKey,
    ProductThreadRegistry,
    readProductThreadRegistry,
    RECENT_PRODUCT_THREADS_LIMIT,
    RecentProductThread,
    recentProductThreads,
    setProductThreadTitle,
    threadStorageSuffix,
    touchProductThread
} from "../../utils/conversationThread";
import { makeNamespace, storageGet, storageSet } from "../../utils/storage";
import { DEFAULT_WIDGET_PERSISTENCE_TTL_MS, getWidgetPersistenceConfig, WIDGET_STORAGE_SCHEMA_VERSION } from "../../utils/widgetPersistence";

interface UseChatPersistenceArgs {
    isWidget: boolean;
    lockedPriceSource?: PriceSource;
    priceSourceLock?: boolean;
    lockedSiteId?: SiteId;
    siteIdLock?: boolean;
    initialSiteId?: SiteId;
    answers: [string, ChatAppResponse][];
    setAnswers: Dispatch<SetStateAction<[string, ChatAppResponse][]>>;
    answerWebCitations: Map<number, WebCitationItem[]>;
    setAnswerWebCitations: Dispatch<SetStateAction<Map<number, WebCitationItem[]>>>;
    isStreaming: boolean;
    processingMessage: string;
    lastQuestionRef: MutableRefObject<string>;
    hydratedFromStorageRef: MutableRefObject<boolean>;
    // ENG-027: drop any pending/visible rating prompt when the conversation is reloaded.
    resetRatingPrompt: () => void;
    setSelectedAnswer: Dispatch<SetStateAction<number>>;
    setQuestionAnswers: Dispatch<SetStateAction<string>>;
    setClearTriggerId: Dispatch<SetStateAction<number>>;
    // ENG-054: the conversation thread on screen (utils/conversationThread.ts); "general" outside the widget.
    threadKey: string;
    // ENG-055: that thread's product (raw SKU), stored in its registry entry; null in the general thread.
    productSku: string | null;
}

// ENG-054: with persistence off, a thread's history is kept in memory only, for the lifetime of
// this loaded widget (like volatileSessions in n8nAdapter.ts), so going back to a thread brings
// its conversation back, matching its still-live session.
const volatileHistory = new Map<string, unknown>();

function historyGet<T>(persisted: boolean, key: string): T | null {
    return persisted ? storageGet<T>(key) : (volatileHistory.get(key) as T | undefined) ?? null;
}

function historySet(persisted: boolean, key: string, value: unknown, ttlMs?: number): void {
    if (persisted) storageSet(key, value, { schemaVersion: WIDGET_STORAGE_SCHEMA_VERSION, ttlMs });
    else volatileHistory.set(key, value);
}

function historyRemove(key: string): void {
    volatileHistory.delete(key);
    try {
        localStorage.removeItem(key);
    } catch {}
}

function deriveUserId(): string {
    try {
        const cfg = (window as any).PublicWidgetConfig;
        return cfg?.userId || "default";
    } catch {
        return "default";
    }
}

function isSiteId(value: unknown): value is SiteId {
    return value === "PUBLIC_GR" || value === "PUBLIC_CY";
}

function isPriceSource(value: unknown): value is PriceSource {
    return value === "store" || value === "web";
}

export function useChatPersistence({
    isWidget,
    lockedPriceSource,
    priceSourceLock,
    lockedSiteId,
    siteIdLock,
    initialSiteId,
    answers,
    setAnswers,
    answerWebCitations,
    setAnswerWebCitations,
    isStreaming,
    processingMessage,
    lastQuestionRef,
    hydratedFromStorageRef,
    resetRatingPrompt,
    setSelectedAnswer,
    setQuestionAnswers,
    setClearTriggerId,
    threadKey,
    productSku
}: UseChatPersistenceArgs) {
    // Recompute the namespace when the userId changes so a re-initialised widget never
    // keeps the previous employee's namespace (cross-user history isolation).
    const userId = deriveUserId();
    const ns = useMemo(() => makeNamespace(window.location.origin, userId, WIDGET_STORAGE_SCHEMA_VERSION), [userId]);
    const priceSourceKey = useMemo(() => ns.key("price_source"), [ns]);
    const siteIdKey = useMemo(() => ns.key("site_id"), [ns]);
    const priceSourceLocked = priceSourceLock ?? lockedPriceSource === "store";
    const siteIdLocked = siteIdLock ?? !!lockedSiteId;

    const deriveInitialPriceSource = (): PriceSource => {
        if (priceSourceLocked) {
            return lockedPriceSource || "web";
        }

        if (!isWidget) {
            const params = new URLSearchParams(window.location.search);
            const priceParam = (params.get("price") || "").toLowerCase();
            if (isPriceSource(priceParam)) {
                return priceParam;
            }
        }

        if (isPriceSource(lockedPriceSource)) {
            return lockedPriceSource;
        }

        try {
            const stored = storageGet<PriceSource>(priceSourceKey);
            if (isPriceSource(stored)) {
                return stored;
            }
        } catch {}

        return lockedPriceSource || "web";
    };

    const deriveInitialSiteId = (): SiteId => {
        if (siteIdLock) {
            if (isSiteId(lockedSiteId)) {
                return lockedSiteId;
            }
            if (isSiteId(initialSiteId)) {
                return initialSiteId;
            }
            return "PUBLIC_GR";
        }

        if (!isWidget) {
            const params = new URLSearchParams(window.location.search);
            const siteIdParam = (params.get("siteId") || params.get("region") || "").toUpperCase();
            if (isSiteId(siteIdParam)) {
                return siteIdParam;
            }
        }

        if (isSiteId(initialSiteId)) {
            return initialSiteId;
        }

        try {
            const stored = storageGet<SiteId>(siteIdKey);
            if (isSiteId(stored)) {
                return stored;
            }
        } catch {}

        return "PUBLIC_GR";
    };

    const [priceSource, setPriceSource] = useState<PriceSource>(deriveInitialPriceSource);
    const [siteId, setSiteId] = useState<SiteId>(deriveInitialSiteId);
    // ENG-054: the (site, thread) whose history `answers` holds; saving waits until it matches.
    const [hydrated, setHydrated] = useState<{ siteId: SiteId; threadKey: string } | null>(null);
    const lastLoadRef = useRef<{ ns: typeof ns; siteId: SiteId; threadKey: string } | null>(null);
    // ENG-055: the product-thread registry of this (user, site) as last read or written; "Recent" comes from it.
    const [productThreads, setProductThreads] = useState<{ ns: typeof ns; siteId: SiteId; registry: ProductThreadRegistry } | null>(null);
    // ENG-055: how many completed messages the thread on screen had when last loaded or stored, so
    // its lastMessageAt moves only when they change, not when it is merely reloaded or reopened.
    const storedCountRef = useRef<{ siteId: SiteId; threadKey: string; count: number } | null>(null);
    // ENG-055: a product title the chip or the intro learned; stored once that thread is loaded.
    const [learnedTitle, setLearnedTitle] = useState<{ sku: string; title: string } | null>(null);
    // ENG-056: the user's last question in the general chat of this (user, site), which names it in the thread bar.
    const [generalLastQuestion, setGeneralLastQuestion] = useState<{ ns: typeof ns; siteId: SiteId; text: string | null } | null>(null);

    // ENG-054: the general thread keeps exactly these keys (existing conversations continue); a
    // product thread appends "__p:<sku>" (threadStorageSuffix).
    const getMessageKeys = (s: SiteId, thread: string) => {
        const suffix = threadStorageSuffix(thread);
        return {
            MSG_KEY: ns.key(`chat_messages_${s}${suffix}`),
            LASTQ_KEY: ns.key(`last_question_${s}${suffix}`),
            WEB_CITATIONS_KEY: ns.key(`chat_web_citations_${s}${suffix}`)
        };
    };

    // ENG-055: the product-thread registry: read it, store it and show it ("Recent"). A v1 entry
    // did not record messages, so its stored history tells (an expired history counts as none).
    const productThreadsKey = (s: SiteId) => ns.key(`${PRODUCT_THREAD_REGISTRY_KEY_PREFIX}${s}`);
    const readProductThreads = (persisted: boolean, s: SiteId): ProductThreadRegistry =>
        readProductThreadRegistry(historyGet(persisted, productThreadsKey(s)), thread => {
            const messages = historyGet<unknown>(persisted, getMessageKeys(s, thread).MSG_KEY);
            return Array.isArray(messages) && messages.length > 0;
        });
    const showProductThreads = (s: SiteId, registry: ProductThreadRegistry) => setProductThreads({ ns, siteId: s, registry });
    const updateProductThreads = (persisted: boolean, s: SiteId, change: (stored: ProductThreadRegistry) => ProductThreadRegistry) => {
        const registry = change(readProductThreads(persisted, s));
        historySet(persisted, productThreadsKey(s), registry);
        showProductThreads(s, registry);
    };

    // ENG-056: the question of the general chat's last completed pair, read from its stored history
    // (no thread suffix) with historyGet, so the TTL and the memory-only mode apply. Widget only.
    const readGeneralLastQuestion = (persisted: boolean, s: SiteId) => {
        if (!isWidget) return;
        const messages = historyGet<unknown>(persisted, getMessageKeys(s, GENERAL_THREAD_KEY).MSG_KEY);
        const last = Array.isArray(messages) ? messages.at(-1) : undefined;
        const text = Array.isArray(last) && typeof last[0] === "string" && last[0].trim() ? last[0] : null;
        setGeneralLastQuestion(previous => (previous?.ns === ns && previous.siteId === s && previous.text === text ? previous : { ns, siteId: s, text }));
    };

    // ENG-054: the MAX_PRODUCT_THREADS most recently opened product threads per (site, user) are
    // kept; the one that falls out loses its messages, last question, web citations and session.
    // The registry has no TTL so a thread can always be cleaned up, even after its history expired.
    // ENG-055: the entry keeps the SKU; a thread that opens without messages leaves "Recent".
    const rememberProductThread = (persisted: boolean, s: SiteId, thread: string, sku: string, hasMessages: boolean) => {
        const { registry, evicted } = touchProductThread(readProductThreads(persisted, s), thread, Date.now(), { sku });
        const stored = hasMessages ? registry : clearProductThreadMessage(registry, thread);
        historySet(persisted, productThreadsKey(s), stored);
        showProductThreads(s, stored);
        for (const old of evicted) {
            const keys = getMessageKeys(s, old);
            historyRemove(keys.MSG_KEY);
            historyRemove(keys.LASTQ_KEY);
            historyRemove(keys.WEB_CITATIONS_KEY);
            clearN8NSession(s, userId, old);
        }
    };

    useEffect(() => {
        const { MSG_KEY, LASTQ_KEY, WEB_CITATIONS_KEY } = getMessageKeys(siteId, threadKey);
        const { enabled, restoreOnInit } = getWidgetPersistenceConfig();
        // ENG-054: switching thread (same user and site) always brings that thread's history back,
        // from storage or, with persistence off, from memory. `enabled`/`restoreOnInit` decide the
        // first load and site changes only, as before.
        const previous = lastLoadRef.current;
        const threadSwitch = !!previous && previous.ns === ns && previous.siteId === siteId && previous.threadKey !== threadKey;
        lastLoadRef.current = { ns, siteId, threadKey };
        let loadedCount = 0;
        try {
            if (!threadSwitch && (!enabled || !restoreOnInit)) {
                // Persistence off or restore disabled: start with a clean conversation.
                setAnswers([]);
                setAnswerWebCitations(new Map());
                lastQuestionRef.current = "";
            } else {
                const persisted = historyGet<[string, ChatAppResponse][]>(enabled, MSG_KEY);
                const lastQ = historyGet<string>(enabled, LASTQ_KEY);
                const persistedCitations = historyGet<[number, WebCitationItem[]][]>(enabled, WEB_CITATIONS_KEY);
                if (persisted && Array.isArray(persisted) && persisted.length > 0) {
                    setAnswers(persisted);
                    loadedCount = persisted.length;
                    hydratedFromStorageRef.current = true;
                } else {
                    setAnswers([]);
                }
                setAnswerWebCitations(Array.isArray(persistedCitations) ? new Map(persistedCitations) : new Map());
                lastQuestionRef.current = lastQ || "";
            }
        } catch {
            setAnswers([]);
            setAnswerWebCitations(new Map());
            lastQuestionRef.current = "";
            loadedCount = 0;
        }
        storedCountRef.current = { siteId, threadKey, count: loadedCount };
        if (isProductThreadKey(threadKey) && productSku) {
            rememberProductThread(enabled, siteId, threadKey, productSku, loadedCount > 0);
        } else if (isWidget) {
            // ENG-055: the general thread shows "Recent" too (read only). Not in the standalone app.
            showProductThreads(siteId, readProductThreads(enabled, siteId));
        }
        readGeneralLastQuestion(enabled, siteId);
        setHydrated({ siteId, threadKey });

        // ENG-054: also on a thread switch, so an armed rating prompt of the thread just left is
        // dropped and never shows in the new one.
        resetRatingPrompt();
        setSelectedAnswer(0);
        setQuestionAnswers("");
        setClearTriggerId(prev => prev + 1);
        clearAllSelections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ns, siteId, threadKey]);

    useEffect(() => {
        if (priceSourceLocked) {
            setPriceSource(lockedPriceSource || "web");
        } else if (!priceSourceLocked) {
            if (isPriceSource(lockedPriceSource)) {
                setPriceSource(lockedPriceSource);
                return;
            }
            try {
                const stored = storageGet<PriceSource>(priceSourceKey);
                if (isPriceSource(stored)) {
                    setPriceSource(stored);
                }
            } catch {}
        }
    }, [lockedPriceSource, priceSourceKey, priceSourceLocked]);

    useEffect(() => {
        if (siteIdLocked) {
            if (isSiteId(lockedSiteId)) {
                setSiteId(lockedSiteId);
            } else if (isSiteId(initialSiteId)) {
                setSiteId(initialSiteId);
            } else {
                setSiteId("PUBLIC_GR");
            }
        } else if (!siteIdLocked) {
            if (isSiteId(initialSiteId)) {
                setSiteId(initialSiteId);
                return;
            }
            try {
                const stored = storageGet<SiteId>(siteIdKey);
                if (isSiteId(stored)) {
                    setSiteId(stored);
                }
            } catch {}
        }
    }, [initialSiteId, lockedSiteId, siteIdKey, siteIdLocked]);

    useEffect(() => {
        if (hydrated?.siteId !== siteId || hydrated.threadKey !== threadKey) {
            return;
        }

        // ENG-054: with persistence off the history still goes to memory (historySet), so a thread
        // switch can bring it back; nothing is written to Web Storage.
        const { enabled, ttlMs, maxTurns } = getWidgetPersistenceConfig();

        try {
            const isIndexStreaming = (idx: number) => isStreaming && idx === answers.length - 1;
            const isCompleted = (pair: [string, ChatAppResponse], idx: number) => {
                if (isIndexStreaming(idx)) return false;
                try {
                    const msg = pair[1]?.choices?.[0]?.message?.content ?? "";
                    return msg && msg !== processingMessage;
                } catch {
                    return false;
                }
            };

            const completed = answers.filter(isCompleted);
            const trimmed = completed.slice(-maxTurns);
            const { MSG_KEY, LASTQ_KEY, WEB_CITATIONS_KEY } = getMessageKeys(siteId, threadKey);
            historySet(enabled, MSG_KEY, trimmed, ttlMs);

            const lastCompletedQ = trimmed.at(-1)?.[0] ?? "";
            historySet(enabled, LASTQ_KEY, lastCompletedQ, ttlMs);

            const firstTrimmedIndex = completed.length - trimmed.length;
            const persistedCitationEntries = trimmed
                .map((_, trimmedIndex) => {
                    const originalIndex = firstTrimmedIndex + trimmedIndex;
                    return [trimmedIndex, answerWebCitations.get(originalIndex) || []] as [number, WebCitationItem[]];
                })
                .filter(([, citations]) => citations.length > 0);
            historySet(enabled, WEB_CITATIONS_KEY, persistedCitationEntries, ttlMs);

            // ENG-055: a product thread's lastMessageAt follows these stored messages: set when they
            // change and are not empty, dropped when they become empty ("New chat").
            const count = completed.length;
            const stored = storedCountRef.current;
            const unchanged = stored?.siteId === siteId && stored.threadKey === threadKey && stored.count === count;
            if (isProductThreadKey(threadKey) && !unchanged) {
                storedCountRef.current = { siteId, threadKey, count };
                updateProductThreads(enabled, siteId, registry =>
                    count > 0 ? markProductThreadMessage(registry, threadKey, Date.now()) : clearProductThreadMessage(registry, threadKey)
                );
            }
            // ENG-056: the general chat's name follows what was just stored ("New chat" empties it).
            if (threadKey === GENERAL_THREAD_KEY) readGeneralLastQuestion(enabled, siteId);
        } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers, answerWebCitations, hydrated, isStreaming, ns, siteId, threadKey]);

    // ENG-055: the title the chip or the intro learned for the thread on screen, written into its
    // registry entry once the thread is loaded (the entry is created then). No lookup of its own.
    useEffect(() => {
        if (!learnedTitle || hydrated?.siteId !== siteId || hydrated.threadKey !== threadKey) return;
        if (!isProductThreadKey(threadKey) || productThreadKey(learnedTitle.sku) !== threadKey) return;
        const { enabled } = getWidgetPersistenceConfig();
        const entry = readProductThreads(enabled, siteId)[threadKey];
        if (!entry || entry.title === learnedTitle.title) return;
        updateProductThreads(enabled, siteId, registry => setProductThreadTitle(registry, threadKey, learnedTitle.title));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [learnedTitle, hydrated, ns, siteId, threadKey]);

    const rememberProductTitle = useCallback((sku: string, title: string) => {
        setLearnedTitle(previous => (previous?.sku === sku && previous.title === title ? previous : { sku, title }));
    }, []);

    // ENG-055: the registry of the user and site on screen (never another user's, for a render).
    const shownProductThreads = productThreads?.ns === ns && productThreads.siteId === siteId ? productThreads.registry : null;

    // ENG-055: "Recent", widget only. A persisted thread's history expires ttlMs after it was last
    // stored and the registry never does, so older entries are left out.
    const recentThreads = useMemo((): RecentProductThread[] => {
        if (!isWidget || !shownProductThreads) return [];
        const { enabled, ttlMs } = getWidgetPersistenceConfig();
        return recentProductThreads(shownProductThreads, threadKey, RECENT_PRODUCT_THREADS_LIMIT, enabled ? Date.now() - ttlMs : -Infinity);
    }, [isWidget, shownProductThreads, threadKey]);

    // ENG-055: the title the registry remembers for a product, if any.
    const productThreadTitle = useCallback(
        (sku: string | null): string | undefined => (sku && shownProductThreads ? shownProductThreads[productThreadKey(sku)]?.title : undefined),
        [shownProductThreads]
    );

    useEffect(() => {
        if (priceSourceLocked) {
            return;
        }
        try {
            storageSet(priceSourceKey, priceSource as string, {
                schemaVersion: WIDGET_STORAGE_SCHEMA_VERSION,
                ttlMs: DEFAULT_WIDGET_PERSISTENCE_TTL_MS
            });
        } catch {}
    }, [priceSource, priceSourceKey, priceSourceLocked]);

    useEffect(() => {
        if (siteIdLocked) {
            return;
        }
        try {
            storageSet(siteIdKey, siteId as string, {
                schemaVersion: WIDGET_STORAGE_SCHEMA_VERSION,
                ttlMs: DEFAULT_WIDGET_PERSISTENCE_TTL_MS
            });
        } catch {}
    }, [siteId, siteIdKey, siteIdLocked]);

    return {
        priceSource,
        setPriceSource,
        priceSourceLocked,
        siteId,
        setSiteId,
        siteIdLocked,
        // ENG-054: the thread whose history is on screen for the current site, null while loading.
        historyThreadKey: hydrated?.siteId === siteId ? hydrated.threadKey : null,
        // ENG-055: the "Recent" list (up to 5 other product threads with messages) and the registry's titles.
        recentThreads,
        productThreadTitle,
        rememberProductTitle,
        // ENG-056: the general chat's last question (never another user's or site's), null without one.
        generalLastQuestion: generalLastQuestion?.ns === ns && generalLastQuestion.siteId === siteId ? generalLastQuestion.text : null
    };
}
